import type { ReactNode } from "react";

export type Language = "en" | "it";

/** The four pages the two stores ask for before they will take an app. */
export type Slug = "privacy" | "terms" | "support" | "delete-account";

export type LegalDocument = {
  title: string;
  /** Shown under the title, and the date the stores will look for. */
  updated: string;
  body: ReactNode;
};

const CONTACT = "francesco.zerbinatti@gmail.com";
const UPDATED_EN = "Last updated: 4 September 2026";
const UPDATED_IT = "Ultimo aggiornamento: 4 settembre 2026";

/**
 * Every legal page, in both languages, in one place.
 *
 * One file rather than eight, because these documents repeat each other on purpose — the
 * disclaimer in the privacy notice has to say the same thing as the one in the terms, and keeping
 * them side by side is what makes that easy to check.
 */
export const documents: Record<Slug, Record<Language, LegalDocument>> = {
  privacy: {
    en: {
      title: "Privacy policy",
      updated: UPDATED_EN,
      body: (
        <>
          <p>
            Gymquisition is made by Dreamsphere. This page says what the app collects, why, where
            it goes and how to make it disappear. It is written to be read, not endured.
          </p>

          <h2>What we collect</h2>
          <p>Only what the app needs to work:</p>
          <ul>
            <li>
              <strong>Your email address</strong>, when you create an account or sign in with
              Google or Apple, so we recognise you next time.
            </li>
            <li>
              <strong>The name you choose</strong>, which is how your coach sees you.
            </li>
            <li>
              <strong>Age, sex, height and weight</strong>, because calories, macros and loads are
              worked out from them. Without those, the app has nothing to build on.
            </li>
            <li>
              <strong>What you log</strong>: workouts, sets, reps, loads, meal plans, supplement
              plans and reminder times.
            </li>
          </ul>
          <p>
            We do not collect your location, your contacts, your photos, or any advertising
            identifier.
          </p>

          <h2>What we do not do</h2>
          <ul>
            <li>No advertising, in the app or anywhere else.</li>
            <li>No tracking of what you do in other apps or on other sites.</li>
            <li>No selling of your data, and no sharing with third parties for their purposes.</li>
            <li>No automated profiling beyond the calculations you see on screen.</li>
          </ul>

          <h2>Where the data lives</h2>
          <p>
            On <strong>Firebase</strong> (Google Ireland Limited), hosting it on our behalf:
            Firebase Authentication for sign-in, Firebase Realtime Database for the content.
            Transfer is encrypted. Google acts as a processor and does not use this data for its
            own purposes. Google&rsquo;s notice:{" "}
            <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer noopener">
              firebase.google.com/support/privacy
            </a>
            .
          </p>

          <h2>Who can see it</h2>
          <p>
            You, until you decide otherwise. The database rules let each account read and write
            only its own data.
          </p>
          <p>
            The one exception is yours to make: give your code to a coach, and that coach can read
            your profile and your plans, and write plans for you. The link is always visible in
            Settings and you can remove it whenever you like.
          </p>

          <h2>How long</h2>
          <p>
            As long as you keep the account. <strong>Settings &rarr; Account &rarr; Delete
            account</strong> removes the sign-in and everything attached to it: profile, workouts,
            plans, reminders. It is immediate and cannot be undone &mdash; we keep no copy.
          </p>

          <h2>Your rights</h2>
          <p>
            You can ask to access, correct, export or delete your data, and object to processing.
            The fastest route for deletion is the button in the app; for anything else write to{" "}
            <a href={`mailto:${CONTACT}`}>{CONTACT}</a> and we will answer within thirty days.
          </p>
          <p>
            If you believe the processing breaches the GDPR you may complain to your national data
            protection authority.
          </p>

          <h2>Children</h2>
          <p>
            Gymquisition is not intended for anyone under 16 and does not knowingly collect their
            data. If we find that we have, we delete it.
          </p>

          <h2>Not medical advice</h2>
          <p>
            The meal and supplement plans are informational material, built on general formulas and
            published literature. They are not a diagnosis, not a prescription, and no substitute
            for a doctor, a dietitian or a blood test. If you have a medical condition, take
            medication, are pregnant or breastfeeding, talk to a doctor before changing your diet
            or starting any supplement.
          </p>

          <h2>Changes</h2>
          <p>
            If we change something we update this page and the date at the top. Changes to what we
            collect are announced inside the app.
          </p>

          <h2>Contact</h2>
          <p>
            Dreamsphere &mdash; <a href={`mailto:${CONTACT}`}>{CONTACT}</a>
          </p>
        </>
      ),
    },
    it: {
      title: "Informativa privacy",
      updated: UPDATED_IT,
      body: (
        <>
          <p>
            Gymquisition è sviluppata da Dreamsphere. Questa pagina dice quali dati l&rsquo;app
            raccoglie, perché, dove finiscono e come farli sparire. È scritta per essere letta, non
            per essere sopportata.
          </p>

          <h2>Cosa raccogliamo</h2>
          <p>Soltanto quello che serve all&rsquo;app per funzionare:</p>
          <ul>
            <li>
              <strong>Il tuo indirizzo email</strong>, quando crei l&rsquo;account o accedi con
              Google o Apple. Serve a riconoscerti al prossimo accesso.
            </li>
            <li>
              <strong>Il nome che scegli</strong>, che è come ti vede il tuo allenatore.
            </li>
            <li>
              <strong>Età, sesso, altezza e peso</strong>, perché calorie, macronutrienti e carichi
              si calcolano da lì. Senza, l&rsquo;app non ha niente su cui costruire.
            </li>
            <li>
              <strong>Quello che registri</strong>: allenamenti, serie, ripetizioni, carichi, piani
              alimentari, piani di integrazione, orari dei promemoria.
            </li>
          </ul>
          <p>
            Non raccogliamo la tua posizione, la rubrica, le foto, né alcun identificativo
            pubblicitario.
          </p>

          <h2>Cosa non facciamo</h2>
          <ul>
            <li>Nessuna pubblicità, dentro l&rsquo;app o altrove.</li>
            <li>Nessun tracciamento di quello che fai su altri siti o altre app.</li>
            <li>Nessuna vendita dei tuoi dati e nessuna cessione a terzi per scopi loro.</li>
            <li>Nessuna profilazione automatica oltre ai calcoli che vedi sullo schermo.</li>
          </ul>

          <h2>Dove finiscono i dati</h2>
          <p>
            Su <strong>Firebase</strong> (Google Ireland Limited), che li ospita per nostro conto:
            Firebase Authentication per l&rsquo;accesso, Firebase Realtime Database per il
            contenuto. Il trasferimento è cifrato. Google agisce come responsabile del trattamento
            e non usa questi dati per scopi propri. Informativa di Google:{" "}
            <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer noopener">
              firebase.google.com/support/privacy
            </a>
            .
          </p>

          <h2>Chi può vederli</h2>
          <p>
            Solo tu, finché non decidi altrimenti. Le regole del database consentono a ciascun
            account di leggere e scrivere unicamente i propri dati.
          </p>
          <p>
            L&rsquo;unica eccezione la crei tu: se dai il tuo codice a un allenatore, quello può
            leggere il tuo profilo e le tue schede, e scriverti piani. Il collegamento si vede
            sempre nelle impostazioni e puoi toglierlo quando vuoi.
          </p>

          <h2>Per quanto tempo</h2>
          <p>
            Finché tieni l&rsquo;account. Da <strong>Impostazioni &rarr; Account &rarr; Elimina
            account</strong> cancelli l&rsquo;accesso e tutti i dati collegati: profilo,
            allenamenti, piani, promemoria. La cancellazione è immediata e non è reversibile &mdash;
            non ne teniamo copia.
          </p>

          <h2>I tuoi diritti</h2>
          <p>
            Puoi chiedere di accedere ai tuoi dati, correggerli, esportarli o cancellarli, e opporti
            al trattamento. La strada più rapida per la cancellazione è il pulsante nell&rsquo;app;
            per tutto il resto scrivi a <a href={`mailto:${CONTACT}`}>{CONTACT}</a> e rispondiamo
            entro trenta giorni.
          </p>
          <p>
            Se pensi che il trattamento violi il GDPR puoi rivolgerti al Garante per la protezione
            dei dati personali (garanteprivacy.it).
          </p>

          <h2>Bambini</h2>
          <p>
            Gymquisition non è pensata per minori di 16 anni e non raccoglie consapevolmente i loro
            dati. Se scopriamo di averne, li cancelliamo.
          </p>

          <h2>Non è un consiglio medico</h2>
          <p>
            I piani alimentari e di integrazione sono materiale informativo, costruito su formule
            generali e su letteratura pubblicata. Non sono una diagnosi, non sono una prescrizione e
            non sostituiscono un medico, un dietista o un esame del sangue. Se hai una patologia,
            prendi farmaci, sei in gravidanza o allatti, parla con un medico prima di cambiare
            alimentazione o iniziare qualunque integratore.
          </p>

          <h2>Modifiche</h2>
          <p>
            Se cambiamo qualcosa aggiorniamo questa pagina e la data in cima. Le modifiche su quali
            dati raccogliamo te le diciamo dentro l&rsquo;app.
          </p>

          <h2>Contatti</h2>
          <p>
            Dreamsphere &mdash; <a href={`mailto:${CONTACT}`}>{CONTACT}</a>
          </p>
        </>
      ),
    },
  },

  terms: {
    en: {
      title: "Terms of use",
      updated: UPDATED_EN,
      body: (
        <>
          <p>
            These terms govern your use of Gymquisition, made by Dreamsphere. Using the app means
            accepting them. If you do not, do not use the app.
          </p>

          <h2>What you get</h2>
          <p>
            A personal, non-transferable licence to use Gymquisition on devices you own. The app is
            free: there is no subscription, no advertising and nothing to buy inside it. You may
            not resell it, take it apart, or pass it off as your own.
          </p>

          <h2>Your account</h2>
          <p>
            You are responsible for keeping your sign-in to yourself and for what is done with it.
            Tell us at once if you think somebody else has it. You may delete your account at any
            time from Settings, which removes your data with it.
          </p>

          <h2>What you put in it</h2>
          <p>
            Your training, meals and measurements stay yours. You give us only what is needed to
            store them and show them back to you, and to the coach you chose to link. You must not
            put anything unlawful in the app or use it to harass anyone.
          </p>

          <h2>Training and nutrition are not medical advice</h2>
          <p>
            Gymquisition is not a medical device. The plans it generates come from general formulas
            and published literature, not from an examination of you. They are not a diagnosis, not
            a prescription, and no substitute for a doctor, a dietitian or a blood test. Training
            with weights carries risk of injury. If you have a medical condition, take medication,
            are pregnant or breastfeeding, talk to a doctor before changing your diet, starting a
            supplement or beginning a programme. You train at your own risk.
          </p>

          <h2>Coaches</h2>
          <p>
            If you give your code to a coach, that coach can read your profile and write plans for
            you. That relationship is between you and them: we neither vet coaches nor take
            responsibility for what they prescribe.
          </p>

          <h2>Availability</h2>
          <p>
            We do our best to keep the app working, but we do not promise it will always be
            available or free of faults. Features may change or be withdrawn.
          </p>

          <h2>Liability</h2>
          <p>
            To the extent the law allows, Dreamsphere is not liable for indirect or consequential
            loss arising from use of the app. Nothing here limits liability for death or personal
            injury caused by negligence, for fraud, or for anything else that cannot be limited by
            law. Your statutory rights as a consumer are unaffected.
          </p>

          <h2>Changes</h2>
          <p>
            We may update these terms; the date at the top says when. Continuing to use the app
            after a change means accepting it.
          </p>

          <h2>Law</h2>
          <p>
            These terms are governed by Italian law. If you are a consumer resident in the EU, you
            keep the protection of the mandatory rules of your own country.
          </p>

          <h2>Contact</h2>
          <p>
            Dreamsphere &mdash; <a href={`mailto:${CONTACT}`}>{CONTACT}</a>
          </p>
        </>
      ),
    },
    it: {
      title: "Condizioni d’uso",
      updated: UPDATED_IT,
      body: (
        <>
          <p>
            Queste condizioni regolano l&rsquo;uso di Gymquisition, sviluppata da Dreamsphere.
            Usare l&rsquo;app significa accettarle. Se non le accetti, non usarla.
          </p>

          <h2>Cosa ricevi</h2>
          <p>
            Una licenza personale e non trasferibile per usare Gymquisition sui dispositivi che
            possiedi. L&rsquo;app è gratuita: nessun abbonamento, nessuna pubblicità, niente da
            comprare al suo interno. Non puoi rivenderla, smontarla o spacciarla per tua.
          </p>

          <h2>Il tuo account</h2>
          <p>
            Sei responsabile di tenere per te le tue credenziali e di quello che viene fatto con
            esse. Avvisaci subito se pensi che qualcun altro le abbia. Puoi eliminare
            l&rsquo;account quando vuoi dalle impostazioni, e con esso i tuoi dati.
          </p>

          <h2>Quello che ci metti dentro</h2>
          <p>
            I tuoi allenamenti, i tuoi pasti e le tue misure restano tuoi. Ci dai solo quanto serve
            per conservarli e mostrarteli, e per mostrarli all&rsquo;allenatore che hai scelto di
            collegare. Non puoi inserire nell&rsquo;app contenuti illeciti né usarla per molestare
            qualcuno.
          </p>

          <h2>Allenamento e alimentazione non sono consigli medici</h2>
          <p>
            Gymquisition non è un dispositivo medico. I piani che genera nascono da formule generali
            e da letteratura pubblicata, non da una visita. Non sono una diagnosi, non sono una
            prescrizione e non sostituiscono un medico, un dietista o un esame del sangue.
            Allenarsi con i pesi comporta un rischio di infortunio. Se hai una patologia, prendi
            farmaci, sei in gravidanza o allatti, parla con un medico prima di cambiare
            alimentazione, iniziare un integratore o cominciare un programma. Ti alleni a tuo
            rischio.
          </p>

          <h2>Allenatori</h2>
          <p>
            Se dai il tuo codice a un allenatore, quello può leggere il tuo profilo e scriverti
            piani. Quel rapporto è tra te e lui: non selezioniamo gli allenatori e non rispondiamo
            di quello che prescrivono.
          </p>

          <h2>Disponibilità</h2>
          <p>
            Facciamo il possibile per tenere l&rsquo;app funzionante, ma non promettiamo che sia
            sempre disponibile o priva di difetti. Le funzioni possono cambiare o essere ritirate.
          </p>

          <h2>Responsabilità</h2>
          <p>
            Nei limiti consentiti dalla legge, Dreamsphere non risponde di danni indiretti o
            consequenziali derivanti dall&rsquo;uso dell&rsquo;app. Nulla qui limita la
            responsabilità per morte o lesioni personali causate da negligenza, per dolo, o per
            quanto altro non sia limitabile per legge. I tuoi diritti di consumatore restano
            impregiudicati.
          </p>

          <h2>Modifiche</h2>
          <p>
            Possiamo aggiornare queste condizioni; la data in cima dice quando. Continuare a usare
            l&rsquo;app dopo una modifica significa accettarla.
          </p>

          <h2>Legge applicabile</h2>
          <p>
            Queste condizioni sono regolate dalla legge italiana. Se sei un consumatore residente
            nell&rsquo;Unione Europea, conservi la tutela delle norme imperative del tuo Paese.
          </p>

          <h2>Contatti</h2>
          <p>
            Dreamsphere &mdash; <a href={`mailto:${CONTACT}`}>{CONTACT}</a>
          </p>
        </>
      ),
    },
  },

  support: {
    en: {
      title: "Support",
      updated: UPDATED_EN,
      body: (
        <>
          <p>
            One person answers this address, and reads every message. Write to{" "}
            <a href={`mailto:${CONTACT}`}>{CONTACT}</a> and you will hear back within a couple of
            working days.
          </p>

          <h2>What to tell us</h2>
          <p>The more of this you include, the faster it gets fixed:</p>
          <ul>
            <li>The phone and the system version (for example: iPhone 13, iOS 18.4).</li>
            <li>The app version, from Settings, at the bottom of the screen.</li>
            <li>What you were doing, what you expected, and what happened instead.</li>
            <li>A screenshot, if there is anything to see.</li>
          </ul>

          <h2>Things people ask</h2>
          <p>
            <strong>The app is in the wrong language.</strong> Settings &rarr; App preferences
            &rarr; Language. It changes immediately and does not follow the phone.
          </p>
          <p>
            <strong>Reminders do not arrive.</strong> Check that notifications are allowed for
            Gymquisition in the phone&rsquo;s settings, and that the switch is on next to the meal
            or the supplement hour. A reminder fires only at an hour that still has something in
            it: move a meal and its reminder moves with it.
          </p>
          <p>
            <strong>Kilograms or pounds.</strong> Settings &rarr; App preferences &rarr; Units.
            Everything already recorded is converted, not rewritten.
          </p>
          <p>
            <strong>I want a coach to see my training.</strong> Give them your coach code, from
            Settings &rarr; Coach. You can revoke it there at any time.
          </p>
          <p>
            <strong>I want my account gone.</strong> See{" "}
            <a href="/app/delete-account">deleting your account</a>.
          </p>

          <h2>Reporting something serious</h2>
          <p>
            If you have found a security problem, or a plan that looks dangerous rather than merely
            wrong, say so in the subject line and it goes to the top of the pile.
          </p>
        </>
      ),
    },
    it: {
      title: "Assistenza",
      updated: UPDATED_IT,
      body: (
        <>
          <p>
            A questo indirizzo risponde una persona sola, e legge ogni messaggio. Scrivi a{" "}
            <a href={`mailto:${CONTACT}`}>{CONTACT}</a> e avrai una risposta entro un paio di giorni
            lavorativi.
          </p>

          <h2>Cosa dirci</h2>
          <p>Più di questo ci metti, prima si risolve:</p>
          <ul>
            <li>Il telefono e la versione di sistema (per esempio: iPhone 13, iOS 18.4).</li>
            <li>La versione dell&rsquo;app, che trovi in fondo alle impostazioni.</li>
            <li>Cosa stavi facendo, cosa ti aspettavi e cosa è successo invece.</li>
            <li>Uno screenshot, se c&rsquo;è qualcosa da vedere.</li>
          </ul>

          <h2>Le domande che arrivano</h2>
          <p>
            <strong>L&rsquo;app è nella lingua sbagliata.</strong> Impostazioni &rarr; Preferenze
            app &rarr; Lingua. Cambia subito e non segue quella del telefono.
          </p>
          <p>
            <strong>I promemoria non arrivano.</strong> Controlla che le notifiche siano consentite
            a Gymquisition nelle impostazioni del telefono, e che l&rsquo;interruttore sia acceso
            accanto al pasto o all&rsquo;ora di integrazione. Un promemoria suona solo a un&rsquo;ora
            in cui c&rsquo;è ancora qualcosa: se sposti un pasto, il promemoria si sposta con lui.
          </p>
          <p>
            <strong>Chilogrammi o libbre.</strong> Impostazioni &rarr; Preferenze app &rarr; Unità.
            Quello che hai già registrato viene convertito, non riscritto.
          </p>
          <p>
            <strong>Voglio che un allenatore veda i miei allenamenti.</strong> Dagli il tuo codice
            coach, da Impostazioni &rarr; Coach. Da lì puoi revocarlo quando vuoi.
          </p>
          <p>
            <strong>Voglio cancellare l&rsquo;account.</strong> Vedi{" "}
            <a href="/app/delete-account/it">come eliminare l&rsquo;account</a>.
          </p>

          <h2>Segnalare qualcosa di grave</h2>
          <p>
            Se hai trovato un problema di sicurezza, o un piano che sembra pericoloso e non
            soltanto sbagliato, scrivilo nell&rsquo;oggetto e passa davanti a tutto il resto.
          </p>
        </>
      ),
    },
  },

  "delete-account": {
    en: {
      title: "Deleting your account",
      updated: UPDATED_EN,
      body: (
        <>
          <p>
            You can delete your Gymquisition account yourself, from inside the app, without asking
            anyone. It takes three taps.
          </p>

          <h2>From the app</h2>
          <ol>
            <li>Open Gymquisition and go to <strong>Settings</strong>.</li>
            <li>Open <strong>Account</strong>.</li>
            <li>
              Choose <strong>Delete account</strong> and confirm. You may be asked to sign in again
              first, which is the phone making sure it is you.
            </li>
          </ol>

          <h2>What goes</h2>
          <p>Everything attached to the account, immediately and for good:</p>
          <ul>
            <li>Your profile: name, email, age, sex, height, weight.</li>
            <li>Every recorded session, every free workout and every set in it.</li>
            <li>Workout plans, meal plans and supplement plans.</li>
            <li>Reminder times, and any link to a coach.</li>
            <li>The sign-in itself, so the address can be used again from scratch.</li>
          </ul>
          <p>
            We keep no copy and no backup of it. Deletion cannot be undone, so export or write down
            anything you want to keep before you press it.
          </p>

          <h2>What stays</h2>
          <p>
            Nothing that identifies you. If you have written to support, that email thread stays in
            the mailbox until it is cleared out in the ordinary course; ask and we will delete it.
          </p>

          <h2>If you cannot reach the button</h2>
          <p>
            Lost the phone, or cannot sign in? Write to{" "}
            <a href={`mailto:${CONTACT}`}>{CONTACT}</a> from the address the account uses, say that
            you want it deleted, and we will do it and confirm within thirty days.
          </p>
        </>
      ),
    },
    it: {
      title: "Eliminare l’account",
      updated: UPDATED_IT,
      body: (
        <>
          <p>
            Puoi eliminare da solo il tuo account Gymquisition, dall&rsquo;app, senza chiedere
            niente a nessuno. Sono tre tocchi.
          </p>

          <h2>Dall&rsquo;app</h2>
          <ol>
            <li>Apri Gymquisition e vai in <strong>Impostazioni</strong>.</li>
            <li>Apri <strong>Account</strong>.</li>
            <li>
              Scegli <strong>Elimina account</strong> e conferma. Potrebbe chiederti di accedere di
              nuovo: è il telefono che si assicura che sia tu.
            </li>
          </ol>

          <h2>Cosa sparisce</h2>
          <p>Tutto quello che è legato all&rsquo;account, subito e per sempre:</p>
          <ul>
            <li>Il profilo: nome, email, età, sesso, altezza, peso.</li>
            <li>Ogni sessione registrata, ogni allenamento libero e ogni serie dentro.</li>
            <li>Schede di allenamento, piani alimentari e piani di integrazione.</li>
            <li>Gli orari dei promemoria e l&rsquo;eventuale collegamento a un allenatore.</li>
            <li>L&rsquo;accesso stesso, così l&rsquo;indirizzo può essere riusato da zero.</li>
          </ul>
          <p>
            Non ne teniamo copia né backup. La cancellazione non è reversibile: esporta o annota
            quello che vuoi conservare prima di premere.
          </p>

          <h2>Cosa resta</h2>
          <p>
            Niente che ti identifichi. Se hai scritto all&rsquo;assistenza, quello scambio di email
            resta nella casella finché non viene ripulita nel normale ricambio; chiedilo e lo
            cancelliamo.
          </p>

          <h2>Se non riesci ad arrivare al pulsante</h2>
          <p>
            Telefono perso, o accesso che non funziona? Scrivi a{" "}
            <a href={`mailto:${CONTACT}`}>{CONTACT}</a> dall&rsquo;indirizzo dell&rsquo;account,
            dicendo che vuoi eliminarlo: lo facciamo noi e ti confermiamo entro trenta giorni.
          </p>
        </>
      ),
    },
  },
};
